import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { CheckCircle2, Download, FileDown, Loader2, Sparkles, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable PDF CTA banner. Two variants:
 *  - variant="full"    → the tall, feature-rich version (used on /dashboard)
 *  - variant="compact" → single-row version (used on /results, /careers/:id)
 */
export default function PDFCTABanner({ variant = "full" }) {
  const { user } = useAuth();
  const [payLoading, setPayLoading] = useState(false);

  const startPayment = async () => {
    if (!window.Razorpay) {
      toast.error("Payment library not loaded. Please refresh the page.");
      return;
    }
    setPayLoading(true);
    try {
      const res = await api.post("/payments/order", { package_id: "career_pdf" });
      const { order_id, amount, currency, key_id, product } = res.data;

      const options = {
        key: key_id,
        amount,
        currency,
        name: "CareerWisdom",
        description: product,
        order_id,
        prefill: { name: user?.name || "", email: user?.email || "" },
        notes: { user_id: user?.user_id, package: "career_pdf" },
        theme: { color: "#002FA7" },
        handler: async (response) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            window.location.href = `/pay/success?order_id=${response.razorpay_order_id}`;
          } catch (e) {
            toast.error("Payment verification failed. Please contact support.");
            setPayLoading(false);
          }
        },
        modal: { ondismiss: () => setPayLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "Please try again."}`);
        setPayLoading(false);
      });
      rzp.open();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.toLowerCase().includes("take the quiz")) {
        toast.error(detail);
        window.location.href = "/quiz";
      } else {
        toast.error(detail || "Could not start checkout. Please try again.");
      }
      setPayLoading(false);
    }
  };

  if (variant === "compact") {
    return (
      <div
        data-testid="pdf-cta-banner-compact"
        className="border border-[#002FA7] bg-[#002FA7] text-white rounded-sm p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 fade-up"
      >
        <div className="w-10 h-10 bg-[#FF2A00] flex items-center justify-center shrink-0">
          <FileDown className="w-5 h-5 text-white" strokeWidth={1.7} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-0.5">
            Take your report with you
          </div>
          <div className="font-heading text-lg md:text-xl font-bold leading-tight">
            Download all 5 careers + curated learning paths &amp; resources as a PDF
          </div>
          <div className="text-xs text-white/70 mt-1">
            10&ndash;12 page personalized report you can share with mentors, parents, or counselors.
            Working professionals: also see the &#8377;101 <b>Job Match Report</b> below.
          </div>
        </div>
        <button
          onClick={startPayment}
          disabled={payLoading}
          data-testid="buy-pdf-button-compact"
          className="bg-white text-[#002FA7] px-5 py-3 font-semibold rounded-sm hover:-translate-y-0.5 transition-transform inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          {payLoading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</>
          ) : (
            <><Download className="w-4 h-4" /> Get PDF · ₹99</>
          )}
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="pdf-cta-banner"
      className="border border-[#002FA7] bg-[#002FA7] text-white rounded-sm overflow-hidden fade-up"
    >
      <div className="p-6 md:p-10">
        <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#FF2A00] flex items-center justify-center shrink-0">
                <FileDown className="w-5 h-5 text-white" strokeWidth={1.7} />
              </div>
              <div className="font-mono text-xs uppercase tracking-widest text-white/60">
                Your complete career report · PDF
              </div>
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold leading-[1.1] tracking-tight mb-3">
              Everything above &mdash; plus deeper career intel &mdash;
              <br className="hidden md:block" />
              in one shareable PDF.
            </h3>
            <p className="text-sm text-white/70 max-w-2xl leading-relaxed">
              A 10&ndash;12 page personalized report to keep, print, and share with the people
              helping you decide. Every career path researched, every next step laid out.
              <br className="hidden md:block" />
              <span className="text-white/60 text-xs">
                Working professional? Add the &#8377;101 Job Match Report on any career detail page.
              </span>
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {[
                ["All 5 AI-matched careers", "Full profile for each — not just the ones you saved."],
                ["Match %, salary in INR & growth outlook", "Fresh India-specific data per NASSCOM & industry sources."],
                ["Courses across Tier 1, 2 & 3 colleges", "IITs to state universities. Formal degrees + free MOOCs."],
                ["7 hand-picked resources per career", "Videos, articles, podcasts, books & communities."],
                ["Every link tappable & shareable", "Direct YouTube / Amazon.in / Reddit — no dead ends."],
                ["Your course tracker snapshot", "Everything you've marked in progress or completed."],
              ].map(([h, body], i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#FACC15] mt-0.5 shrink-0" strokeWidth={2.2} />
                  <div>
                    <div className="text-sm font-semibold leading-snug text-white">{h}</div>
                    <div className="text-xs text-white/70 leading-snug">{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:w-72 lg:shrink-0 border-t lg:border-t-0 lg:border-l border-white/10 pt-6 lg:pt-0 lg:pl-10">
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-5xl font-black tracking-tighter">₹99</span>
              <span className="text-xs text-white/50 font-mono uppercase tracking-widest">one-time</span>
            </div>
            <div className="mt-1 text-xs text-white/50 font-mono">Less than one filter coffee.</div>
            <button
              onClick={startPayment}
              disabled={payLoading}
              data-testid="buy-pdf-button"
              className="mt-5 w-full bg-white text-[#0A0A0A] px-6 py-3.5 font-semibold rounded-sm hover:-translate-y-0.5 transition-transform inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {payLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</>
              ) : (
                <><Download className="w-4 h-4" /> Get my report</>
              )}
            </button>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/pdf/sample`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="preview-pdf-link"
              className="mt-3 w-full text-center inline-flex items-center justify-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors py-2"
            >
              <Eye className="w-3.5 h-3.5" /> See sample PDF first
            </a>
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 text-xs text-white/50">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                <span>UPI, card, netbanking &middot; secured by Razorpay</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-white/50">
                <Sparkles className="w-3.5 h-3.5 shrink-0" />
                <span>Powered by Claude Sonnet 4.5 AI</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
